import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarOff } from 'lucide-react';
import { get, put, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateOnly, humanize } from '@/lib/utils';
import { DataTable, Dialog, FilterBar, FilterField } from '@/components/ui/layout';
import { Alert, Badge, Button, Field, Select, StatusBadge, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { useLeaveEmployeeOptions } from './employee-options';
import { dayLabel } from './hr-shared';

interface LeaveRow {
  id: string;
  type: string;
  fromDate: string;
  toDate: string;
  days: number;
  reason: string | null;
  status: string;
  rosteredDates: string[];
  employee: { id: string; name: string; employeeNo: string };
}

interface Balance {
  employeeId: string;
  type: string;
  entitlementDays: number;
  takenDays: number;
  remainingDays: number;
}

const LEAVE_TYPES = ['casual', 'sick', 'annual', 'unpaid', 'maternity', 'paternity'];
const LEAVE_STATUSES = ['pending', 'approved', 'rejected', 'cancelled'];
const ENTITLED_TYPES = new Set(['casual', 'sick', 'annual']);
const ENTITLEMENT: Record<string, number> = { casual: 10, sick: 8, annual: 14 };

function balanceHint(row: LeaveRow, balances: Balance[] | undefined): string | null {
  if (!ENTITLED_TYPES.has(row.type)) return null;
  const match = balances?.find((b) => b.employeeId === row.employee.id && b.type === row.type);
  const taken = match?.takenDays ?? 0;
  const entitlement = match?.entitlementDays ?? ENTITLEMENT[row.type];
  return `${taken} of ${entitlement} ${row.type} days used this year`;
}

function PersonCell({ row }: { row: LeaveRow }) {
  return (
    <div>
      <p>{row.employee.name}</p>
      <p className="text-xs font-normal text-muted-foreground">{row.employee.employeeNo}</p>
    </div>
  );
}

function DatesCell({ row }: { row: LeaveRow }) {
  return (
    <div>
      <p className="tabular">
        {dateOnly(row.fromDate)}
        {row.fromDate !== row.toDate && ` → ${dateOnly(row.toDate)}`}
      </p>
      <p className="text-xs text-muted-foreground">
        {row.days} {row.days === 1 ? 'day' : 'days'}
      </p>
    </div>
  );
}

function RejectLeaveDialog({ request, onClose }: { request: LeaveRow; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reject = useMutation({
    mutationFn: () => put(`/hr/leave/${request.id}`, { status: 'rejected', notes: notes.trim() || undefined }),
    onSuccess: () => {
      toast.success('Leave rejected');
      void queryClient.invalidateQueries({ queryKey: ['leave'] });
      onClose();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      icon={CalendarOff}
      title="Reject leave"
      description={`${request.employee.name} · ${humanize(request.type)} · ${dateOnly(request.fromDate)} → ${dateOnly(request.toDate)}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="destructive" loading={reject.isPending} onClick={() => reject.mutate()}>
            Reject
          </Button>
        </>
      }
    >
      <Field label="Reason" hint="Kept on the request so the person knows why">
        <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} autoFocus />
      </Field>
      {error && (
        <Alert variant="destructive" className="mt-3">
          {error}
        </Alert>
      )}
    </Dialog>
  );
}

export function LeaveTab() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const approver = can('hr.leave.approve');
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [rejecting, setRejecting] = useState<LeaveRow | null>(null);
  const employees = useLeaveEmployeeOptions(can('hr.employee.read', 'hr.employee.manage'));
  const year = new Date().getFullYear();

  const pending = useQuery({
    queryKey: ['leave', 'pending'],
    queryFn: () => get<LeaveRow[]>(`/hr/leave${qs({ status: 'pending', limit: 200 })}`),
    enabled: approver,
  });

  const all = useQuery({
    queryKey: ['leave', 'all', status, type, employeeId],
    queryFn: () =>
      get<LeaveRow[]>(`/hr/leave${qs({ status: status || undefined, type: type || undefined, employeeId: employeeId || undefined, limit: 200 })}`),
  });

  const balances = useQuery({
    queryKey: ['leave', 'balances', year],
    queryFn: () => get<Balance[]>(`/hr/leave/balances${qs({ year })}`),
    enabled: approver || can('hr.employee.read'),
  });

  const approve = useMutation({
    mutationFn: (id: string) => put(`/hr/leave/${id}`, { status: 'approved' }),
    onSuccess: () => {
      toast.success('Leave approved');
      void queryClient.invalidateQueries({ queryKey: ['leave'] });
    },
    onError: (error) => toast.error('Could not approve', describeError(error)),
  });

  const filtered = status || type || employeeId;

  return (
    <>
      {approver && (
        <section className="mb-6">
          <div className="mb-2 flex items-center gap-2">
            <h3 className="text-sm font-semibold">Awaiting decision</h3>
            <Badge variant={pending.data?.length ? 'warning' : 'muted'}>{pending.data?.length ?? 0}</Badge>
          </div>
          <DataTable
            dense
            rows={pending.data}
            loading={pending.isLoading}
            refreshing={pending.isFetching}
            error={pending.error}
            rowKey={(row) => row.id}
            empty="Nothing waiting for a decision."
            columns={[
              { header: 'Person', sort: (row) => row.employee.name, cell: (row) => <PersonCell row={row} /> },
              { header: 'Type', sort: (row) => row.type, cell: (row) => <Badge variant="secondary">{humanize(row.type)}</Badge> },
              { header: 'Dates', sort: (row) => row.fromDate, cell: (row) => <DatesCell row={row} /> },
              { header: 'Reason', hideBelow: 'md', cell: (row) => <span className="line-clamp-2 max-w-xs text-muted-foreground">{row.reason ?? '—'}</span> },
              {
                header: 'Roster',
                sort: (row) => row.rosteredDates.length,
                cell: (row) =>
                  row.rosteredDates.length ? (
                    <Badge variant="warning" title={row.rosteredDates.map((d) => dayLabel(d)).join(', ')}>
                      Rostered {row.rosteredDates.map((d) => dayLabel(d, { day: 'numeric', month: 'short' })).slice(0, 2).join(', ')}
                      {row.rosteredDates.length > 2 && ` +${row.rosteredDates.length - 2}`}
                    </Badge>
                  ) : (
                    <span className="text-xs text-muted-foreground">Clear</span>
                  ),
              },
              {
                header: 'Balance',
                hideBelow: 'lg',
                cell: (row) => <span className="text-xs text-muted-foreground">{balanceHint(row, balances.data) ?? '—'}</span>,
              },
            ]}
            rowActions={(row) => (
              <>
                <Button size="sm" loading={approve.isPending && approve.variables === row.id} onClick={() => approve.mutate(row.id)}>
                  Approve
                </Button>
                <Button size="sm" variant="outline" onClick={() => setRejecting(row)}>
                  Reject
                </Button>
              </>
            )}
          />
        </section>
      )}

      <div className="mb-2 flex items-center gap-2">
        <h3 className="text-sm font-semibold">All requests</h3>
      </div>
      <FilterBar
        trailing={
          can('hr.self.read', 'hr.employee.manage') && (
            <Button variant="outline" onClick={() => navigate('/people/leave/new')}>
              Request leave
            </Button>
          )
        }
      >
        <FilterField label="Status">
          <Select className="w-40" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            {LEAVE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {humanize(s)}
              </option>
            ))}
          </Select>
        </FilterField>
        <FilterField label="Type">
          <Select className="w-40" value={type} onChange={(e) => setType(e.target.value)}>
            <option value="">All</option>
            {LEAVE_TYPES.map((t) => (
              <option key={t} value={t}>
                {humanize(t)}
              </option>
            ))}
          </Select>
        </FilterField>
        <FilterField label="Person">
          <Select className="w-56" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
            <option value="">Everyone</option>
            {employees.data?.map((employee) => (
              <option key={employee.id} value={employee.id}>
                {employee.name}
              </option>
            ))}
          </Select>
        </FilterField>
        {filtered && (
          <FilterField>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setStatus('');
                setType('');
                setEmployeeId('');
              }}
            >
              Reset
            </Button>
          </FilterField>
        )}
      </FilterBar>

      <DataTable
        rows={all.data}
        loading={all.isLoading}
        refreshing={all.isFetching}
        error={all.error}
        rowKey={(row) => row.id}
        empty="No leave requests match."
        columns={[
          { header: 'Person', sort: (row) => row.employee.name, cell: (row) => <PersonCell row={row} /> },
          { header: 'Type', sort: (row) => row.type, cell: (row) => <Badge variant="secondary">{humanize(row.type)}</Badge> },
          { header: 'Dates', sort: (row) => row.fromDate, cell: (row) => <DatesCell row={row} /> },
          { header: 'Reason', hideBelow: 'md', cell: (row) => <span className="line-clamp-2 max-w-xs text-muted-foreground">{row.reason ?? '—'}</span> },
          { header: 'Status', sort: (row) => row.status, cell: (row) => <StatusBadge status={row.status} /> },
        ]}
        rowActions={
          approver
            ? (row) =>
                row.status === 'pending' && (
                  <>
                    <Button size="sm" loading={approve.isPending && approve.variables === row.id} onClick={() => approve.mutate(row.id)}>
                      Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setRejecting(row)}>
                      Reject
                    </Button>
                  </>
                )
            : undefined
        }
      />

      {rejecting && <RejectLeaveDialog request={rejecting} onClose={() => setRejecting(null)} />}
    </>
  );
}
